/// <reference types="@types/google.accounts" />
import { Injectable } from '@angular/core';
import { Observable, fromEvent, of, throwError } from 'rxjs';
import { map, catchError, shareReplay, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// Type declaration for gapi (Google API JavaScript client library)
declare const gapi: any;

/**
 * Service to handle Google Sign-In script loading and initialization.
 * Ensures the Google script is loaded before attempting to use Google APIs.
 */
@Injectable({
    providedIn: 'root',
})
export class GoogleSignInService {
    private readonly GOOGLE_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
    private readonly GOOGLE_API_SCRIPT_URL = 'https://apis.google.com/js/api.js';
    
    private googleScriptLoaded$: Observable<typeof google> | null = null;
    private googleApiScriptLoaded$: Observable<typeof gapi> | null = null;

    /**
     * Loads the Google Sign-In script if not already loaded.
     * Returns an Observable that emits when the script is ready.
     */
    loadGoogleSignInScript(): Observable<typeof google> {
        // If already loaded, return immediately
        if (typeof google !== 'undefined' && google?.accounts?.id) {
            return of(google);
        }

        // If already loading, return the existing Observable
        if (this.googleScriptLoaded$) {
            return this.googleScriptLoaded$;
        }

        // Check if script is already in the DOM
        const existingScript = document.querySelector(`script[src="${this.GOOGLE_SCRIPT_URL}"]`);
        if (existingScript) {
            // Script is in DOM but not loaded yet, wait for it
            this.googleScriptLoaded$ = this.waitForGoogleScript();
            return this.googleScriptLoaded$;
        }

        // Load the script dynamically
        this.googleScriptLoaded$ = this.loadScript(this.GOOGLE_SCRIPT_URL).pipe(
            map(() => {
                if (typeof google === 'undefined' || !google?.accounts?.id) {
                    throw new Error('Google Sign-In script failed to load');
                }
                return google;
            }),
            shareReplay(1),
            catchError((error) => {
                this.googleScriptLoaded$ = null; // Reset on error to allow retry
                return throwError(() => new Error(`Failed to load Google Sign-In script: ${error.message}`));
            })
        );

        return this.googleScriptLoaded$;
    }

    /**
     * Loads the Google API script if not already loaded.
     * Returns an Observable that emits when the script is ready.
     */
    loadGoogleApiScript(): Observable<typeof gapi> {
        // If already loaded, return immediately
        if (typeof gapi !== 'undefined') {
            return of(gapi);
        }

        // If already loading, return the existing Observable
        if (this.googleApiScriptLoaded$) {
            return this.googleApiScriptLoaded$;
        }

        // Check if script is already in the DOM
        const existingScript = document.querySelector(`script[src="${this.GOOGLE_API_SCRIPT_URL}"]`);
        if (existingScript) {
            // Script is in DOM but not loaded yet, wait for it
            this.googleApiScriptLoaded$ = this.waitForGoogleApiScript();
            return this.googleApiScriptLoaded$;
        }

        // Load the script dynamically
        this.googleApiScriptLoaded$ = this.loadScript(this.GOOGLE_API_SCRIPT_URL).pipe(
            map(() => {
                if (typeof gapi === 'undefined') {
                    throw new Error('Google API script failed to load');
                }
                return gapi;
            }),
            shareReplay(1),
            catchError((error) => {
                this.googleApiScriptLoaded$ = null; // Reset on error to allow retry
                return throwError(() => new Error(`Failed to load Google API script: ${error.message}`));
            })
        );

        return this.googleApiScriptLoaded$;
    }

    /**
     * Initializes Google Sign-In with the provided configuration.
     * Ensures the script is loaded before initializing.
     */
    initializeGoogleSignIn(config: {
        client_id: string;
        auto_select?: boolean;
        login_hint?: string;
        callback: (response: google.accounts.id.CredentialResponse) => void;
    }): Observable<void> {
        return this.loadGoogleSignInScript().pipe(
            map((google) => {
                google.accounts.id.initialize({
                    client_id: config.client_id,
                    auto_select: config.auto_select ?? false,
                    ...(config.login_hint && { login_hint: config.login_hint }),
                    callback: config.callback,
                });
            }),
            take(1)
        );
    }

    /**
     * Renders a Google Sign-In button in the specified element.
     * Ensures the script is loaded before rendering.
     */
    renderButton(
        element: HTMLElement,
        options: google.accounts.id.GsiButtonConfiguration
    ): Observable<void> {
        return this.loadGoogleSignInScript().pipe(
            map((google) => {
                google.accounts.id.renderButton(element, options);
            }),
            take(1)
        );
    }

    /**
     * Stores a credential using Google's credential store.
     * Ensures the script is loaded before storing.
     */
    storeCredential(
        credential: { id: string; password: string },
        callback?: () => void
    ): Observable<void> {
        // PasswordCredential (part of the Credential Management API) only exists in Chromium
        // browsers. On Firefox/Safari, google.accounts.id.storeCredential() still tries to call
        // navigator.credentials.store() with a plain object instead, which throws inside Google's
        // own script ("parameter 1 is not of type 'Credential'"). Skip it there instead of erroring.
        if (!('PasswordCredential' in window)) {
            return of(void 0);
        }

        return this.loadGoogleSignInScript().pipe(
            map((google) => {
                if (google.accounts.id.storeCredential) {
                    google.accounts.id.storeCredential(credential, callback);
                } else {
                    console.warn('storeCredential is not available');
                }
            }),
            take(1)
        );
    }

    /**
     * Initializes Google OAuth2 Code Client.
     * Ensures both Google Sign-In and API scripts are loaded.
     */
    initCodeClient(config: {
        client_id: string;
        scope: string;
        callback: (response: google.accounts.oauth2.CodeResponse) => void;
    }): Observable<google.accounts.oauth2.CodeClient> {
        return this.loadGoogleSignInScript().pipe(
            map((google) => {
                if (!google.accounts?.oauth2?.initCodeClient) {
                    throw new Error('Google OAuth2 Code Client is not available');
                }
                return google.accounts.oauth2.initCodeClient(config);
            }),
            take(1)
        );
    }

    /**
     * Checks if Google Sign-In is available.
     */
    isGoogleSignInAvailable(): boolean {
        return typeof google !== 'undefined' && !!google?.accounts?.id;
    }

    /**
     * Checks if Google API is available.
     */
    isGoogleApiAvailable(): boolean {
        return typeof gapi !== 'undefined';
    }

    /**
     * Loads a script dynamically and returns an Observable that completes when loaded.
     */
    private loadScript(src: string): Observable<Event> {
        return new Observable((observer) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.defer = true;

            script.onload = (event) => {
                observer.next(event);
                observer.complete();
            };

            script.onerror = (error) => {
                observer.error(error);
            };

            document.head.appendChild(script);

            // Cleanup function
            return () => {
                // Note: We don't remove the script on unsubscribe as it may be used elsewhere
            };
        });
    }

    /**
     * Waits for Google script to be available if it's already in the DOM.
     */
    private waitForGoogleScript(): Observable<typeof google> {
        return new Observable((observer) => {
            const checkInterval = setInterval(() => {
                if (typeof google !== 'undefined' && google?.accounts?.id) {
                    clearInterval(checkInterval);
                    observer.next(google);
                    observer.complete();
                }
            }, 50);

            // Timeout after 10 seconds
            setTimeout(() => {
                clearInterval(checkInterval);
                if (typeof google === 'undefined' || !google?.accounts?.id) {
                    observer.error(new Error('Timeout waiting for Google Sign-In script to load'));
                }
            }, 10000);
        });
    }

    /**
     * Waits for Google API script to be available if it's already in the DOM.
     */
    private waitForGoogleApiScript(): Observable<typeof gapi> {
        return new Observable((observer) => {
            const checkInterval = setInterval(() => {
                if (typeof gapi !== 'undefined') {
                    clearInterval(checkInterval);
                    observer.next(gapi);
                    observer.complete();
                }
            }, 50);

            // Timeout after 10 seconds
            setTimeout(() => {
                clearInterval(checkInterval);
                if (typeof gapi === 'undefined') {
                    observer.error(new Error('Timeout waiting for Google API script to load'));
                }
            }, 10000);
        });
    }
}

